import React, { useState, useMemo } from "react";
import FloatingInput from "../../components/input/FloatingInput";

const ITEMS_PER_PAGE = 10;

const ParcelSearchResultsModal = ({ parcelSearchResults = [], onClose, onSelect, title = "Search Results" }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  // Filter results based on search term
  const filteredResults = useMemo(() => {
    if (!searchTerm.trim()) return parcelSearchResults;

    const term = searchTerm.toLowerCase();
    return parcelSearchResults.filter((parcel) => {
      const searchableFields = [
        parcel.Address,
        parcel.City,
        parcel.State,
        parcel.Zip,
        parcel["Owner Name"],
        parcel.Apn,
        parcel["Prop Type"],
      ];

      return searchableFields.some((field) =>
        field?.toString().toLowerCase().includes(term)
      );
    });
  }, [parcelSearchResults, searchTerm]);

  // Pagination logic
  const totalPages = Math.ceil(filteredResults.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedResults = filteredResults.slice(
    startIndex,
    startIndex + ITEMS_PER_PAGE
  );

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handleRowSelect = (parcel) => {
    if (onSelect && typeof onSelect === 'function') {
      onSelect(parcel);
      // Delay modal closing to allow state updates to process
      setTimeout(() => {
        onClose();
      }, 100);
    }
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    // Adjust start page if we're near the end
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    // Add first page and ellipsis if needed
    if (startPage > 1) {
      pages.push(
        <button
          key={1}
          onClick={() => handlePageChange(1)}
          className="pagination-btn"
        >
          1
        </button>
      );
      if (startPage > 2) {
        pages.push(<span key="ellipsis1" className="pagination-ellipsis">...</span>);
      }
    }

    // Add visible pages
    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={`pagination-btn ${i === currentPage ? "active" : ""}`}
        >
          {i}
        </button>
      );
    }

    // Add last page and ellipsis if needed
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pages.push(<span key="ellipsis2" className="pagination-ellipsis">...</span>);
      }
      pages.push(
        <button
          key={totalPages}
          onClick={() => handlePageChange(totalPages)}
          className="pagination-btn"
        >
          {totalPages}
        </button>
      );
    }

    return (
      <div className="pagination-controls">
        <button
          onClick={handlePrevPage}
          disabled={currentPage === 1}
          className="pagination-nav-btn"
        >
          Previous
        </button>
        <div className="pagination-pages">{pages}</div>
        <button
          onClick={handleNextPage}
          disabled={currentPage === totalPages}
          className="pagination-nav-btn"
        >
          Next
        </button>
      </div>
    );
  };

  return (
    <div className="popup-data-result model-popup-style parcel-search-modal">
      <div className="model-body">
        

        {/* Search Filter */}
        <div className="search-filter-section">
          <div className="form-group">
            <FloatingInput
              type="search"
              label="Search parcels..."
              placeholder="Search by address, city, owner name, APN, etc."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1); // Reset to first page when searching
              }}
            />
          </div>
        </div>

        {/* Results Table */}
        {paginatedResults.length > 0 ? (
          <>
            <div className="parcel-results-table-container tbl-type-a">
              <table className="parcel-results-table">
                <thead>
                  <tr>
                    <th>Address</th>
                    <th>Unit</th>
                    <th>City</th>
                    <th>State</th>
                    <th>Zip</th>
                    <th>Owner Name</th>
                    <th>Sold In</th>
                    <th>APN</th>
                    <th>Property Type</th>
                    <th>Sqft</th>
                    <th>Value</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedResults.map((parcel, idx) => (
                    <tr
                      key={idx}
                      onClick={() => handleRowSelect(parcel)}
                      style={{ cursor: 'pointer' }}
                      className="parcel-row-hover"
                    >
                      <td>{parcel.Address || "N/A"}</td>
                      <td>{parcel.Unit || "N/A"}</td>
                      <td>{parcel.City || "N/A"}</td>
                      <td>{parcel.State || "N/A"}</td>
                      <td>{parcel.Zip || "N/A"}</td>
                      <td>{parcel["Owner Name"] || "N/A"}</td>
                      <td>{parcel["Sold in"] || "N/A"}</td>
                      <td>{parcel.Apn || "N/A"}</td>
                      <td>{parcel["Prop Type"] || "N/A"}</td>
                      <td>{parcel.Sqft || "N/A"}</td>
                      <td>{parcel.$ ? `$${parcel.$.toLocaleString()}` : "N/A"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

          <div className="paginationalign">
            <div className="search-results-info">
              <p style={{ fontSize: '12px' }}>
                Showing {paginatedResults.length} of {filteredResults.length} results
                {searchTerm && ` (filtered from ${parcelSearchResults.length} total)`}
              </p>
            </div>
            {/* Pagination */}
            {renderPagination()}
            </div>
          </>
        ) : (
          <div className="no-results">
            <p className="font-medium">No parcels found</p>
            <p className="text-sm mt-2">
              {searchTerm
                ? "Try adjusting your search terms"
                : "No parcels match your criteria"}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ParcelSearchResultsModal;